import React, { useState, useCallback, useRef, useEffect } from 'react';
import './TipTapToolbar.css';

/**
 * Custom toolbar for TipTap editor.
 * Renders formatting buttons that interact with the editor instance.
 */
const TipTapToolbar = ({
  editor,
  enableImageUpload = false,
  enableAttachments = false,
  imageInputRef,
  videoInputRef,
  fileInputRef,
}) => {
  const [showTableMenu, setShowTableMenu] = useState(false);
  const tableMenuRef = useRef(null);
  const textColorRef = useRef(null);
  const bgColorRef = useRef(null);

  // Close table menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target)) {
        setShowTableMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!editor) return null;

  const isInTable = editor.isActive('table');

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleImageUpload = () => {
    if (imageInputRef?.current) {
      imageInputRef.current.click();
    }
  };

  const handleVideoUpload = () => {
    if (videoInputRef?.current) {
      videoInputRef.current.click();
    }
  };

  const handleAttachment = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  const handleTextColor = () => {
    if (textColorRef.current) {
      textColorRef.current.click();
    }
  };

  const handleBgColor = () => {
    if (bgColorRef.current) {
      bgColorRef.current.click();
    }
  };

  return (
    <div className="tiptap-toolbar">
      {/* Header dropdown */}
      <div className="toolbar-group">
        <select
          className="toolbar-select"
          style = {{ marginBottom: '0px' }}
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : '0'
          }
          onChange={(e) => {
            const level = parseInt(e.target.value, 10);
            if (level === 0) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level }).run();
            }
          }}
        >
          <option value="0">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
      </div>

      <div className="toolbar-divider" />

      {/* Text formatting */}
      <div className="toolbar-group">
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)"
        >
          <span style={{ textDecoration: 'underline' }}>U</span>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
        >
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </ToolbarButton>
      </div>

      <div className="toolbar-divider" />

      {/* Color */}
      <div className="toolbar-group">
        <div className="toolbar-color-wrapper">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleTextColor}
            title="Text color"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M11 4L6 20h2.5l1.2-4h4.6l1.2 4H18L13 4h-2zm-.5 10L12 8.7 13.5 14h-3z" fill="currentColor"/>
              <rect x="3" y="20" width="18" height="3" rx="1" fill={editor.getAttributes('textStyle').color || 'currentColor'} />
            </svg>
          </button>
          <input
            ref={textColorRef}
            type="color"
            className="toolbar-color-input"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </div>
        <div className="toolbar-color-wrapper">
          <button
            type="button"
            className="toolbar-btn"
            onClick={handleBgColor}
            title="Background color"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15a1.49 1.49 0 000 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21z" fill="currentColor"/>
              <rect x="3" y="20" width="18" height="3" rx="1" fill={editor.getAttributes('highlight')?.color || '#ffff00'} />
            </svg>
          </button>
          <input
            ref={bgColorRef}
            type="color"
            className="toolbar-color-input"
            value={editor.getAttributes('highlight')?.color || '#ffff00'}
            onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
          />
        </div>
      </div>

      <div className="toolbar-divider" />

      {/* Lists */}
      <div className="toolbar-group">
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
          </svg>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
          </svg>
        </ToolbarButton>
      </div>

      <div className="toolbar-divider" />

      {/* Block elements */}
      <div className="toolbar-group">
        <ToolbarButton
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
          </svg>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('codeBlock')}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          title="Code block"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
          </svg>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Inline code"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
          </svg>
        </ToolbarButton>
      </div>

      <div className="toolbar-divider" />

      {/* Insert elements */}
      <div className="toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal rule"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="2" y="11" width="20" height="2" rx="1" />
          </svg>
        </ToolbarButton>

        {/* Table */}
        <div className="toolbar-dropdown-wrapper" ref={tableMenuRef}>
          <ToolbarButton
            active={isInTable}
            onClick={() => setShowTableMenu(!showTableMenu)}
            title="Table"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </ToolbarButton>
          {showTableMenu && (
            <div className="toolbar-dropdown-menu">
              {!isInTable ? (
                <button
                  className="dropdown-menu-item"
                  onClick={() => {
                    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                    setShowTableMenu(false);
                  }}
                >
                  Insert 3x3 table
                </button>
              ) : (
                <>
                  <button className="dropdown-menu-item" onClick={() => { editor.chain().focus().addRowAfter().run(); setShowTableMenu(false); }}>
                    Add row below
                  </button>
                  <button className="dropdown-menu-item" onClick={() => { editor.chain().focus().addRowBefore().run(); setShowTableMenu(false); }}>
                    Add row above
                  </button>
                  <button className="dropdown-menu-item" onClick={() => { editor.chain().focus().addColumnAfter().run(); setShowTableMenu(false); }}>
                    Add column right
                  </button>
                  <button className="dropdown-menu-item" onClick={() => { editor.chain().focus().addColumnBefore().run(); setShowTableMenu(false); }}>
                    Add column left
                  </button>
                  <div className="dropdown-menu-divider" />
                  <button className="dropdown-menu-item danger" onClick={() => { editor.chain().focus().deleteRow().run(); setShowTableMenu(false); }}>
                    Delete row
                  </button>
                  <button className="dropdown-menu-item danger" onClick={() => { editor.chain().focus().deleteColumn().run(); setShowTableMenu(false); }}>
                    Delete column
                  </button>
                  <button className="dropdown-menu-item danger" onClick={() => { editor.chain().focus().deleteTable().run(); setShowTableMenu(false); }}>
                    Delete table
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Link */}
        <ToolbarButton
          active={editor.isActive('link')}
          onClick={handleLink}
          title={editor.isActive('link') ? 'Remove link' : 'Add link'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
          </svg>
        </ToolbarButton>
      </div>

      {/* Image & Attachment buttons */}
      {(enableImageUpload || enableAttachments) && (
        <>
          <div className="toolbar-divider" />
          <div className="toolbar-group">
            {enableImageUpload && (
              <ToolbarButton onClick={handleImageUpload} title="Insert image">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
              </ToolbarButton>
            )}
            {enableImageUpload && (
              <ToolbarButton onClick={handleVideoUpload} title="Insert video">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              </ToolbarButton>
            )}
            {enableAttachments && (
              <ToolbarButton onClick={handleAttachment} title="Attach file">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                </svg>
              </ToolbarButton>
            )}
          </div>
        </>
      )}

      <div className="toolbar-divider" />

      {/* Clear formatting */}
      <div className="toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Clear formatting"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.27 5L2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.27 5zM6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6z"/>
          </svg>
        </ToolbarButton>
      </div>
    </div>
  );
};

/**
 * Reusable toolbar button
 */
const ToolbarButton = ({ active, onClick, title, children }) => (
  <button
    type="button"
    className={`toolbar-btn ${active ? 'active' : ''}`}
    onClick={onClick}
    title={title}
    tabIndex={-1}
  >
    {children}
  </button>
);

export default TipTapToolbar;
